"use client";

import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/auth-and-permissions-context";
import { formatCurrency } from "@/lib/utils";
import { AddOnFormDialog } from "./add-on-form-dialog";
import ConfirmDialog from "@/components/admin/shared/confirm-dialog";
import { toast } from "sonner";

interface AddOn {
  id: number;
  venue_id: number;
  name: string;
  add_on_type: string;
  pricing_id: number;
  pricing: {
    price: number;
    vat_percentage: number;
  };
  created_at: string;
  updated_at: string;
}

interface AddOnListProps {
  venueId: number;
}

export function AddOnList({ venueId }: AddOnListProps) {
  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAddOn, setSelectedAddOn] = useState<AddOn | null>(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [addOnToDelete, setAddOnToDelete] = useState<AddOn | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useAuth();

  const isSuperAdmin = user?.role === "super_admin";
  const isBookingAdmin = user?.role === "booking_staff";
  const canModify = isSuperAdmin || isBookingAdmin;

  useEffect(() => {
    fetchAddOns();
  }, [venueId]);

  const fetchAddOns = async () => {
    try {
      const response = await fetch(`/api/venues/${venueId}/add-ons`);
      if (!response.ok) throw new Error("Failed to fetch add-ons");
      const data = await response.json();
      setAddOns(data);
    } catch (error) {
      console.error("Error fetching add-ons:", error);
      toast.error("Failed to load add-ons");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!addOnToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(
        `/api/venues/${venueId}/add-ons/${addOnToDelete.id}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete add-on");
      }

      toast.success("Add-on deleted successfully");

      await fetchAddOns();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete add-on");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setAddOnToDelete(null);
    }
  };

  const handleEdit = (addOn: AddOn) => {
    setSelectedAddOn(addOn);
    setShowFormDialog(true);
  };

  const handleCreate = () => {
    setSelectedAddOn(null);
    setShowFormDialog(true);
  };

  const handleFormSuccess = () => {
    setShowFormDialog(false);
    setSelectedAddOn(null);
    fetchAddOns();
  };

  const getAddOnTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      food: "Food",
      drink: "Drink",
      service: "Service",
      equipment: "Equipment",
      other: "Other",
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Add-Ons</CardTitle>
              <CardDescription>
                {loading
                  ? "Loading..."
                  : `${addOns.length} result${addOns.length !== 1 ? "s" : ""}`}
              </CardDescription>
            </div>
            {canModify && (
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add New Add-On
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>VAT %</TableHead>
                {canModify && (
                  <TableHead className="text-right">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {addOns.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={canModify ? 5 : 4}
                    className="text-center text-muted-foreground"
                  >
                    No add-ons found for this venue
                  </TableCell>
                </TableRow>
              ) : (
                addOns.map((addOn) => (
                  <TableRow key={addOn.id}>
                    <TableCell className="font-medium">{addOn.name}</TableCell>
                    <TableCell>
                      {getAddOnTypeLabel(addOn.add_on_type)}
                    </TableCell>
                    <TableCell>{formatCurrency(addOn.pricing.price)}</TableCell>
                    <TableCell>{addOn.pricing.vat_percentage}%</TableCell>
                    {canModify && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(addOn)}
                          className="mr-2"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setAddOnToDelete(addOn);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {showFormDialog && (
        <AddOnFormDialog
          open={showFormDialog}
          onOpenChange={setShowFormDialog}
          venueId={venueId}
          addOn={selectedAddOn}
          onSuccess={handleFormSuccess}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Add-On"
        description="Are you sure you want to delete this add-on? Add-ons that are linked to active or future bookings cannot be deleted."
        itemName={addOnToDelete?.name}
        itemSubtext={`Type: ${addOnToDelete?.add_on_type}`}
        confirmText="Delete"
        confirmLoadingText="Deleting..."
        isLoading={isDeleting}
      />
    </>
  );
}
